// Constantes de perigos específicos por tipo
// Baseado nas normas ISO 12100

export interface PerigosEspecificos {
  potenciaisConsequencias: string[];
  origem: string[];
}

export const PERIGOS_ESPECIFICOS: Record<string, PerigosEspecificos> = {
  mecanico: {
    origem: [
      'aceleração, desaceleração',
      'cantos vivos',
      'aproximação de um elemento móvel a uma parte fixa',
      'corte de peças',
      'elementos elásticos',
      'queda de objetos',
      'gravidade',
      'altura a partir do solo',
      'alta pressão',
      'instabilidade',
      'energia cinética',
      'mobilidade da máquina',
      'elementos móveis',
      'elementos rotativos',
      'superfície áspera, escorregadia',
      'arestas cortantes',
      'energia armazenada',
      'vácuo',
    ],
    potenciaisConsequencias: [
      'atropelamentos',
      'arremessos',
      'esmagamento',
      'corte e mutilação',
      'segurar e prender',
      'enroscar',
      'fricção e abrasão',
      'impacto',
      'injeção',
      'raspagem',
      'escorregamento, tropeço e queda',
      'perfuração',
      'sufocamento',
    ],
  },
  eletrico: {
    origem: [
      'arcos',
      'fenômenos eletromagnéticos',
      'partes vivas',
      'baixa rigidez dielétrica',
      'partes vivas sob condições de falha',
      'curto-circuito',
      'radiação térmica',
    ],
    potenciaisConsequencias: [
      'queimadura',
      'efeitos químicos',
      'efeitos em implantes médicos',
      'eletrocussão',
      'queda ou arremesso',
      'incêndio',
      'projeção de fagulhas',
      'choque',
    ],
  },
  termico: {
    origem: [
      'explosão',
      'incêndio',
      'objetos ou materiais com alta ou baixa temperatura',
      'radiação proveniente de fontes quentes',
    ],
    potenciaisConsequencias: [
      'queimadura',
      'desidratação',
      'desconforto',
      'congelamento',
      'danos causados pela radiação de fontes quentes',
      'escaldo',
    ],
  },
  ruido: {
    origem: [
      'fenômeno de cavitação',
      'sistema de exaustão',
      'vazamento de gás em alta velocidade',
      'processos de produção (estampagem, corte, etc.)',
      'partes móveis',
      'superfícies em atrito',
      'peças rotativas desbalanceadas',
      'ruídos pneumáticos',
      'peças desgastadas',
    ],
    potenciaisConsequencias: [
      'desconforto',
      'perda de consciência',
      'perda de equilíbrio',
      'perda permanente de audição',
      'estresse',
      'zumbido',
      'cansaço',
      'outros como (por exemplo mecânicos, elétricos) em decorrência da interferência em comunicações ou sinais acústicos',
    ],
  },
  vibracao: {
    origem: [
      'fenômeno de cavitação',
      'desalinhamento de partes móveis',
      'equipamentos móveis',
      'superfícies em atrito',
      'peças rotativas desbalanceadas',
      'equipamentos que vibram',
      'peças desgastadas',
    ],
    potenciaisConsequencias: [
      'desconforto',
      'morbidade lombar',
      'disfunções neurológicas',
      'disfunções osteoarticulares',
      'traumas na coluna',
      'disfunções vasculares',
    ],
  },
  radiacao: {
    origem: [
      'fontes de radiação ionizante',
      'radiações eletromagnéticas de baixa frequência',
      'radiação ótica (infravermelho, visível ou ultravioleta), incluindo laser',
      'radiações eletromagnéticas em radiofrequência',
    ],
    potenciaisConsequencias: [
      'queimadura',
      'danos nos olhos e pele',
      'efeitos na capacidade reprodutiva',
      'mutações',
      'dor de cabeça, insônia, etc',
      'efeitos cancerígenos',
    ],
  },
  quimico: {
    origem: [
      'aerossóis',
      'agentes biológicos e microbiológicos (virais ou bacterianos)',
      'combustíveis',
      'poeira',
      'explosivos',
      'fibras',
      'inflamáveis',
      'fluídos',
      'fumos',
      'gases',
      'misturas',
      'oxidantes',
    ],
    potenciaisConsequencias: [
      'dificuldades para respiração, asfixia',
      'câncer',
      'corrosão',
      'efeitos na capacidade reprodutiva',
      'explosões',
      'fogo',
      'infecções',
      'mutações',
      'envenenamento',
      'sensibilização',
    ],
  },
  ergonomico: {
    origem: [
      'acesso',
      'projeto ou localização de indicadores, indicadores e painéis',
      'projeto, localização ou identificação de dispositivos de comando',
      'esforços',
      'cintilação, brilho, sombra, efeito estroboscópio',
      'luz local',
      'sobrecarga mental, ociosidade',
      'postura',
      'atividade repetitiva',
      'visibilidade',
    ],
    potenciaisConsequencias: [
      'desconforto',
      'fadiga',
      'distúrbios músculo esqueléticos',
      'estresse',
      'outros como (por exemplo...)',
    ],
  },
  ambiental: {
    origem: [
      'poeira ou neblina',
      'pertubação eletromagnética',
      'descargas atmosféricas',
      'umidade',
      'poluição',
      'neve',
      'temperatura',
      'água',
      'vento',
      'falta de oxigênio',
    ],
    potenciaisConsequencias: [
      'queimadura',
      'doenças brandas',
      'queda ou escorregamento',
      'asfixia',
      'qualquer outra consequência do efeito causado por fontes de perigos da máquina ou partes da mesma',
    ],
  },
  combinacao: {
    origem: [
      'por exemplo, atividades repetitivas + esforços + ambientes em alta temperatura',
    ],
    potenciaisConsequencias: [
      'por exemplo, desidratação, perda de consciência e ataque cardíaco',
    ],
  },
};

/** Assinatura mínima do `t` do `useI18n()` — a constante não importa React. */
type Traduzir = (chave: string) => string;

const RAIZ_TEXTO = 'apreciacaoTabelas.perigos.texto';

/**
 * Origem e consequência são gravadas na apreciação como o próprio texto em
 * português, então a lista continua sendo de strings e a tradução acontece só
 * na exibição: o texto vira chave (`cantos vivos` → `cantosVivos`) e o
 * dicionário responde no idioma da tela. Texto que não está no dicionário —
 * escrito à mão numa apreciação antiga — sai como está.
 */
export function chaveTextoPerigo(texto: string): string {
  const palavras = normalizarAcentos(texto)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  return palavras
    .map((palavra, i) => (i === 0 ? palavra : palavra.charAt(0).toUpperCase() + palavra.slice(1)))
    .join('');
}

/** Conjunto com tudo que o dicionário cobre — evita `t()` de chave inexistente. */
const TEXTOS_CONHECIDOS = new Set(
  Object.values(PERIGOS_ESPECIFICOS).flatMap((p) => [...p.origem, ...p.potenciaisConsequencias])
);

export function traduzirTextoPerigo(t: Traduzir, texto: string): string {
  return TEXTOS_CONHECIDOS.has(texto) ? t(`${RAIZ_TEXTO}.${chaveTextoPerigo(texto)}`) : texto;
}

/**
 * Função auxiliar para normalizar acentos, substituindo caracteres acentuados por versões sem acento
 */
function normalizarAcentos(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Função para obter perigos específicos de um tipo
 */
export function obterPerigosEspecificos(tipoPerigo: string): PerigosEspecificos | undefined {
  // Primeiro normalizar acentos, depois converter para minúsculas e remover caracteres especiais
  const tipoNormalizado = normalizarAcentos(tipoPerigo.toLowerCase()).replace(/[^a-z0-9]/g, '');

  // Mapeamento de variações de nomes (agora usando versões sem acento)
  const mapeamento: Record<string, string> = {
    'mecanico': 'mecanico',
    'eletrico': 'eletrico',
    'termico': 'termico',
    'ruido': 'ruido',
    'vibracao': 'vibracao',
    'radiacao': 'radiacao',
    'quimico': 'quimico',
    'biologico': 'quimico',
    'ergonomico': 'ergonomico',
    'ambiental': 'ambiental',
    'combinacao': 'combinacao',
  };

  const tipoMapeado = mapeamento[tipoNormalizado] || tipoNormalizado;
  return PERIGOS_ESPECIFICOS[tipoMapeado];
}

